import React from 'react';

import {CidIdMap} from 'notebook/notes/NoteConstants';
import {FlowchartConfigWithMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/flowchartConfigAutosaveManagerCallbacks';
import {FlowchartConfigMetadata} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import {MakeFieldsRequired} from 'util/Types';

import {ProcessFlowchartConfigForEditorWithLatestVersionGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessFlowchartConfigForEditorWithLatestVersionGraphQL';
import {ProcessFlowchartTaskSchemaNodeAdapter} from './ProcessFlowchartNodeAdapter';

export type FlowchartConfigMetadataWithRootTaskSchema = MakeFieldsRequired<
  FlowchartConfigMetadata,
  'rootTaskSchema'
>;

export interface FlowchartConfigWithMetadataAndRootTaskSchema extends FlowchartConfigWithMetadata {
  metadata: FlowchartConfigMetadataWithRootTaskSchema;
}

export type RemoveNodeConfirmationParams =
  | {isMultiNodeRemoval: false; nodeName: string; metadata: FlowchartConfigMetadataWithRootTaskSchema}
  | {isMultiNodeRemoval: true; metadata: FlowchartConfigMetadataWithRootTaskSchema};

export interface RemoveNodeConfirmationContent {
  title: string;
  subtitle: React.ReactNode;
}

export interface ProcessFlowchartAdditionalPersistedData {
  /**
   * A map of client IDs to API IDs that were updated during the save. This is helpful when an object's ID has changed
   * after the save (ex: new version), and we need to update the flowchart config with the new ID value by
   * finding the object by its client ID.
   */
  cidToIdMap: CidIdMap;
  currentVersionId: string | null;
}

/**
 * Base type for adapters for the processes editor. This is used both for the root and output nodes
 * in a flowchart, and is used to provide additional functionality to the editor based on the flowchart type.
 */
export abstract class ProcessFlowchartAdapter<
  TFrontendApplicationData,
  TNetworkApplicationData,
  TValuableServerData = null,
> extends ProcessFlowchartTaskSchemaNodeAdapter<TFrontendApplicationData, TNetworkApplicationData> {
  /**
   * The user-facing name of the flowchart type to display for autosaver notifications
   */
  static readonly AUTOSAVE_FRIENDLY_NAME: string = 'Flowchart';
  /**
   * For this flowchart editor, any additional actions need to take before the flowchart config is saved.
   * @param flowchartConfig
   */
  abstract onSaveStart(flowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema): void;

  /**
   * After a successful save, extract any valuable data from the server response. This will be passed into the
   * onSaveComplete method for further processing.
   * @param serverResponse
   */
  extractValuableDataFromServerResponse?(
    serverResponse: ProcessFlowchartConfigForEditorWithLatestVersionGraphQL
  ): TValuableServerData;

  /**
   * For this flowchart editor, any additional actions need to take after the flowchart config is saved.
   *
   * This is especially useful for incorporating the cidToIdMap back into the latest flowchart config,
   * if versioned objects have changed after save and need IDs to be replaced.
   * @param persistedFlowchartConfig - The persisted flowchart config.
   * @param additionalPersistedData - Any additional persisted data returned from the network save to use in
   * the frontend to build the new draft flowchart config.
   * @returns The updated flowchart config with that incorporates additional save data, or null if no update is needed.
   */
  abstract onSaveComplete(
    persistedFlowchartConfig: FlowchartConfigWithMetadataAndRootTaskSchema,
    additionalPersistedData: ProcessFlowchartAdditionalPersistedData & {
      valuableServerData: TValuableServerData;
    }
  ): Promise<FlowchartConfigWithMetadataAndRootTaskSchema> | null;

  /**
   * Provides confirmation content for removing node(s) from the flowchart.
   * Override this method to provide custom confirmation content.
   *
   * @param params - Either {isMultiNodeRemoval: false, nodeName, metadata} for single node removal,
   *                 or {isMultiNodeRemoval: true, metadata} for multiple nodes.
   * @returns The title and subtitle to display in the confirmation modal.
   */
  getRemoveNodeConfirmationContent(params: RemoveNodeConfirmationParams): RemoveNodeConfirmationContent {
    return {
      title: 'Remove from flowchart?',
      subtitle: params.isMultiNodeRemoval ? (
        'Removing these items will also remove all connections to or from them:'
      ) : (
        <>
          Removing <strong>{params.nodeName}</strong> will also remove all connections to or from it.
        </>
      ),
    };
  }
}
